import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@client/components/ui/dialog';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Label } from '@client/components/ui/label';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { AlertCircle, Send, Users, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@client/components/ui/radio-group';

interface InviteAdvisorDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: (email: string, enabledClientAccess: boolean) => void;
  validateEmail: (email: string) => boolean;
  emailError?: string | null;
  onEmailChange?: (email: string) => void;
  isMutating?: boolean;
}

export function InviteAdvisorDialog({
  open,
  onClose,
  onInvite,
  validateEmail,
  emailError,
  onEmailChange,
  isMutating = false,
}: InviteAdvisorDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [emailInput, setEmailInput] = useState('');
  const [clientAccess, setClientAccess] = useState<'yes' | 'no'>('yes');

  // Reset dialog state when it opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setEmailInput('');
      setClientAccess('yes');
    }
  }, [open]);

  const handleClose = () => {
    if (isMutating) {
      return;
    }
    setStep(1);
    setEmailInput('');
    setClientAccess('yes');
    onClose();
  };

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    if (onEmailChange) {
      onEmailChange(value);
    }
  };

  const handleContinueFromStep1 = () => {
    if (emailInput && !emailError && validateEmail(emailInput)) {
      setStep(2);
    }
  };

  const handleContinueFromStep2 = () => {
    setStep(3);
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const handleBackToStep2 = () => {
    setStep(2);
  };

  const handleSendInvite = () => {
    if (emailInput && !emailError) {
      onInvite(emailInput, clientAccess === 'yes');
    }
  };

  const getStepName = () => {
    if (step === 1) return 'Advisor Email';
    if (step === 2) return 'Client Access';
    if (step === 3) return 'Review & Confirm';
    return '';
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">Invite Advisor</DialogTitle>
          <DialogDescription className="text-gray-700">{getStepName()}</DialogDescription>
        </DialogHeader>

        {/* Step 1: Email Input */}
        {step === 1 && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Mail className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Enter the advisor's email address
                  </h3>
                  <p className="text-sm text-gray-700">
                    We'll send them an invitation to join your team
                  </p>
                </div>
              </div>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label
                    htmlFor="email"
                    className="text-primary-dark"
                  >
                    Advisor Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="advisor@example.com"
                    value={emailInput}
                    onChange={e => handleEmailInputChange(e.target.value)}
                    onKeyDown={e => {
                      if (
                        e.key === 'Enter' &&
                        emailInput &&
                        !emailError &&
                        validateEmail(emailInput)
                      ) {
                        handleContinueFromStep1();
                      }
                    }}
                  />
                </div>
                {emailError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{emailError}</AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleContinueFromStep1}
                disabled={!emailInput || !!emailError || !validateEmail(emailInput)}
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 2: Client Access */}
        {step === 2 && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Users className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Do you need access to this advisor's clients?
                  </h3>
                  <p className="text-sm text-gray-700">
                    This determines view/edit access to this advisor's clients
                  </p>
                </div>
              </div>
              <RadioGroup
                value={clientAccess}
                onValueChange={value => setClientAccess(value as 'yes' | 'no')}
                className="gap-2"
              >
                <div className="flex items-center space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                  <RadioGroupItem
                    value="yes"
                    id="client-access-yes"
                  />
                  <Label
                    htmlFor="client-access-yes"
                    className="flex-1 cursor-pointer text-base font-normal text-gray-900"
                  >
                    Yes, I need to view and edit their clients
                  </Label>
                </div>
                <div className="flex items-center space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                  <RadioGroupItem
                    value="no"
                    id="client-access-no"
                  />
                  <Label
                    htmlFor="client-access-no"
                    className="flex-1 cursor-pointer text-base font-normal text-gray-900"
                  >
                    No, I don't need client access
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleBackToStep1}
              >
                Back
              </Button>
              <Button onClick={handleContinueFromStep2}>Continue</Button>
            </DialogFooter>
          </>
        )}

        {/* Step 3: Final Confirmation */}
        {step === 3 && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <CheckCircle className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">Review and Confirm</h3>
                  <p className="text-sm text-gray-700">
                    Please verify the invitation details before sending
                  </p>
                </div>
              </div>

              <div className="space-y-4 rounded-md border  p-4">
                {/* Email */}
                <div>
                  <p className="text-sm font-medium text-gray-700">Advisor Email</p>
                  <p className="text-base text-gray-900">{emailInput}</p>
                </div>

                {/* Client Access */}
                <div>
                  <p className="text-sm font-medium text-gray-700">Client Access</p>
                  <p className="text-base text-gray-900">
                    {clientAccess === 'yes'
                      ? 'Yes, you will have access to their clients'
                      : 'No, you will not have access to their clients'}
                  </p>
                </div>
              </div>

              {/* Invitation Info */}
              <div className="bg-primary-50 border-primary-100 mt-2 rounded-md border p-4">
                <p className="text-primary-dark text-sm">
                  Note: You will have access to view the advisor's subscription and usage statistics
                  by default.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleBackToStep2}
                disabled={isMutating}
              >
                Back
              </Button>
              <Button
                onClick={handleSendInvite}
                disabled={isMutating}
              >
                {isMutating ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-1 h-4 w-4" />
                )}
                {isMutating ? 'Sending...' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
